import { useState } from "react";
import { ChevronDown, X, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

/**
 * MultiSelect — Liste déroulante à choix multiples.
 *
 * Props :
 *   options : [{ value, label, icon?, color? }]
 *   selected: Set<value> | Array<value>  — valeurs cochées (l'appelant gère son propre Set/Array)
 *   onChange: (next: Set<value>) => void
 *   label   : string  — placeholder quand rien n'est coché
 *   width   : string Tailwind (ex. "w-[200px]")
 *   searchable : bool — affiche un champ de recherche en haut (utile pour 36 districts)
 *   testid  : data-testid prefix
 */
export function MultiSelect({
    options,
    selected,
    onChange,
    label = "Sélectionner",
    width = "w-[200px]",
    searchable = false,
    testid = "multiselect",
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const sel = selected instanceof Set ? selected : new Set(selected || []);

    const toggle = (val) => {
        const next = new Set(sel);
        if (next.has(val)) next.delete(val);
        else next.add(val);
        onChange(next);
    };
    const clearAll = (e) => {
        e.stopPropagation();
        onChange(new Set());
    };
    const filtered = searchable && q.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase().trim()))
        : options;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`${width} h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 flex items-center justify-between gap-2 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300`}
                    data-testid={`${testid}-trigger`}
                >
                    <span className="truncate text-left flex-1">
                        {sel.size === 0
                            ? <span className="text-slate-500">{label}</span>
                            : sel.size === 1
                                ? options.find((o) => o.value === [...sel][0])?.label || `${sel.size} sélectionné`
                                : `${sel.size} sélectionnés`}
                    </span>
                    {sel.size > 0 && (
                        <span
                            role="button"
                            tabIndex={-1}
                            onClick={clearAll}
                            className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            data-testid={`${testid}-clear`}
                            aria-label="Effacer la sélection"
                        >
                            <X size={14} />
                        </span>
                    )}
                    <ChevronDown size={14} className="text-slate-400 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[320px]"
                align="start"
                data-testid={`${testid}-popover`}
            >
                {searchable && (
                    <div className="p-2 border-b border-slate-200">
                        <Input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher…"
                            className="h-8 text-sm"
                            data-testid={`${testid}-search`}
                        />
                    </div>
                )}
                <div className="max-h-[280px] overflow-y-auto py-1">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-500 text-center">Aucun résultat</div>
                    ) : (
                        filtered.map((opt) => {
                            const checked = sel.has(opt.value);
                            const Icon = opt.icon;
                            return (
                                <label
                                    key={opt.value}
                                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                                    data-testid={`${testid}-option-${opt.value}`}
                                >
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={() => toggle(opt.value)}
                                        className="shrink-0"
                                    />
                                    {Icon && (
                                        <Icon
                                            size={13}
                                            className="shrink-0"
                                            style={opt.color ? { color: opt.color } : undefined}
                                        />
                                    )}
                                    <span className="flex-1 truncate">{opt.label}</span>
                                    {checked && <Check size={12} className="text-slate-400 shrink-0" />}
                                </label>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
